import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { faClose, faTruckFast } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { useCreateCompany } from "../../../hooks/Company/useCreateCompany"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import InputField from "../../../components/InputField/InputField"
import { useError } from "../../../context/useErrorContext"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { usePermission } from "../../../hooks/Permissions/usePermission"

interface FreeShippingSettingsPupopProps {
  onClose: () => void
}

export const FreeShippingSettingsPupop: React.FC<FreeShippingSettingsPupopProps> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { handleSaveCompany } = useSaveCompany()

  const { setError } = useError()

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const {
    handleInitialValue,
    handleSubmitForm,
    isFreeShippingEnabled,
    freeShippingMinimumOrderValue,
    isFixedShippingFeeEnabled,
    fixedShippingFeeValue,
    handleFixedShippingFeeValue,
    handleFreeShippingMinimumOrderValue,
    setIsFreeShippingEnabled,
    setIsFixedShippingFeeEnabled,
  } = useCreateCompany()

  useEffect(() => {
    handleInitialValue(companyMemory)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTrySave = async () => {
    const data = handleSubmitForm()

    if (
      (data.fixedShippingFeeValue === 0 && data.isFixedShippingFeeEnabled) ||
      (data.freeShippingMinimumOrderValue === 0 && data.isFreeShippingEnabled)
    ) {
      setError(t("free_shipping.invalid_value_title"), t("free_shipping.invalid_value_description"))

      return
    }

    await handleSaveCompany(data, () => {
      handleInitialValue(companyMemory)
      onClose()
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("free_shipping.title")}
              icon={faTruckFast}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.mystore.mycompany.freeshippingsettingspupop.1"
                    variant="global"
                    title={t("helpPopup:global.deliverySettings.title")}
                    description={t("helpPopup:global.deliverySettings.description")}
                    bullets={t("helpPopup:global.deliverySettings.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.freeShippingSettingsPupop.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="flex flex-col gap-4 p-2"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()

                void handleTrySave()
              }}
            >
              <SimpleToggle
                title={t("free_shipping.enable_free_shipping")}
                value={isFreeShippingEnabled}
                disabled={canEdite}
                onChange={(value) => setIsFreeShippingEnabled(value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.freeShippingSettingsPupop.toggle.enableFreeShipping"
              />

              {isFreeShippingEnabled && (
                <>
                  <InputField
                    type="number"
                    title={t("free_shipping.minimum_order_value")}
                    value={freeShippingMinimumOrderValue}
                    onChange={handleFreeShippingMinimumOrderValue}
                    placeholder={t("free_shipping.minimum_order_value")}
                    required
                    disable={!isFreeShippingEnabled || canEdite}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.freeShippingSettingsPupop.input.minimumOrderValue"
                  />

                  <div className={defaultStyle.divisionBar}></div>
                </>
              )}

              <SimpleToggle
                title={t("free_shipping.enable_fixed_shipping")}
                value={isFixedShippingFeeEnabled}
                disabled={canEdite}
                onChange={(value) => setIsFixedShippingFeeEnabled(value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.freeShippingSettingsPupop.toggle.enableFixedShipping"
              />

              {isFixedShippingFeeEnabled && (
                <InputField
                  type="number"
                  title={t("free_shipping.fixed_shipping_value")}
                  value={fixedShippingFeeValue}
                  onChange={handleFixedShippingFeeValue}
                  placeholder={t("free_shipping.fixed_shipping_value")}
                  required
                  disable={!isFixedShippingFeeEnabled || canEdite}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.freeShippingSettingsPupop.input.fixedShippingValue"
                />
              )}

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCompany}>
                  <Button
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.freeShippingSettingsPupop.button.save"
                  />
                </CanView>

                <GetSpace right={5} />

                <Button
                  label={t("buttons:button_register")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGRegisterColor}
                  onClick={handleEnableRegister}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.freeShippingSettingsPupop.button.register"
                />
              </section>
            </form>

            {isRegisterEnable && (
              <ViewRegister id={companyMemory.id} typeEntity="CompanyEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
